import React from "react";
import { Link } from "react-router-dom";
import { Globe } from "lucide-react";
import registry from "@/locales/registry.json";
import { fr } from "@/locales/fr";
import { es } from "@/locales/es";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

// English page keeps its own aria-label text (unchanged, additive);
// French/Spanish pages get their own string, centralized in
// locales/fr.js and locales/es.js per the "single file" rule rather
// than hardcoded here.
const ARIA_LABEL = { en: "Select language", fr: fr.pickerAriaLabel, es: es.pickerAriaLabel };

/**
 * Header language picker.
 *
 * Built from the locale registry (`src/locales/registry.json`) — three
 * entries today (en, fr, es). Each option is a plain route link (English
 * -> "/", French -> "/fr/", Spanish -> "/es/"), never a JS-only handler,
 * so the destination is a real navigable URL. No auto-redirect by
 * browser language, no cookie, no stored preference — language is
 * whichever route you're on.
 *
 * Built on Radix's DropdownMenu primitive (via the shadcn wrapper), which
 * implements the WAI-ARIA menu pattern out of the box: Enter/Space opens
 * and activates, Arrow Up/Down moves focus, Escape closes, focus returns
 * to the trigger on close.
 */
export const LocalePicker = ({ current = "en" }) => {
  const currentEntry = registry.find((e) => e.code === current) || registry[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex items-center gap-1.5 font-ui text-sm text-creamtext/85 hover:text-gold transition-colors duration-200 tap-extend outline-none focus-visible:text-gold"
        data-testid="locale-picker-trigger"
        aria-label={ARIA_LABEL[current] || ARIA_LABEL.en}
      >
        <Globe size={14} strokeWidth={2} aria-hidden="true" />
        {currentEntry.name}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-ink border border-gold/30 text-creamtext min-w-[9rem]"
        data-testid="locale-picker-menu"
      >
        {registry.map((entry) => (
          <DropdownMenuItem
            key={entry.code}
            asChild
            className="font-ui text-sm text-creamtext/85 focus:bg-gold/15 focus:text-gold data-[disabled]:opacity-100 cursor-pointer"
            data-testid={`locale-picker-option-${entry.code}`}
          >
            <Link
              to={entry.path}
              aria-current={entry.code === current ? "page" : undefined}
              className={entry.code === current ? "text-gold" : undefined}
            >
              {entry.name}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LocalePicker;
